import { useCallback } from "react";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";

/**
 * Resolves the name to show for one persona, per viewer.
 *
 * A Stream user profile is a global singleton, so the server can only put there what EVERY viewer
 * may see: the real name for public/business personas, the @handle for private ones (ADR-0007).
 * The remaining arm of the disclosure rule — a private persona's real name IS shown to their
 * mutual followers and to the people they messaged first (App ADR-0011) — is viewer-relative and
 * therefore resolved here, from the disclosed-names list this client already holds. That list came
 * from an endpoint that applied the rule server-side, so the client only ever learns names it was
 * entitled to.
 *
 * The viewer's OWN persona is the one case the list cannot cover (nobody discloses to themselves),
 * so it is resolved from the chat token, which carries the persona's display name for exactly this
 * purpose: a private user still sees their own name on their own messages while Stream, and
 * everyone else, carries their @handle. The token is cached app-wide (the unread badge holds it),
 * so this adds no request.
 *
 * Falls back to whatever Stream carries, which is the fail-closed direction: an unresolved persona
 * keeps its @handle rather than leaking a name.
 */
export const useDisclosedNameResolver = () => {
  const user = useLoggedInUser();
  const { data: chatToken } = useChatTokenQuery(
    !!user,
    user?.activeContextPublicID,
  );
  const { data: disclosedNames } = useDisclosedNamesMapQuery();

  return useCallback(
    (userId: string | undefined, streamName: string | undefined) => {
      if (!userId) {
        return streamName;
      }

      if (userId === chatToken?.userId) {
        return chatToken.userName || streamName;
      }

      return disclosedNames?.get(userId) || streamName;
    },
    [chatToken, disclosedNames],
  );
};
